import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import { useTranslation } from "react-i18next";
import MoneyIcon from "@mui/icons-material/Money";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

export default function PaymentMethods({ value = "cash", provider = "", onChange = () => {} }) {
  const { t, i18n } = useTranslation("payment_methods");
  // determine language/direction
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const dir = lang === "ar" ? "rtl" : "ltr";

  // value: "cash" | "online"
  // provider: "visa" | "mastercard" | "wallet"
  const selected = value === "cash" ? "cash" : (provider || "visa");

  const handleChange = (v) => {
    if (v === "cash") {
      onChange({ payment_method: "cash", payment_provider: "" });
    } else {
      onChange({ payment_method: "online", payment_provider: v });
    }
  };

  const paymentOptions = [
    {
      id: "cash",
      label: t("cash", { defaultValue: "كاش" }),
      icon: MoneyIcon,
      color: "#2e7d32",
      bgColor: "success.50",
    },
    {
      id: "visa",
      label: t("visa", { defaultValue: "فيزا" }),
      icon: CreditCardIcon,
      color: "#1976d2",
      bgColor: "info.light",
    },
    {
      id: "mastercard",
      label: t("mastercard", { defaultValue: "ماستر كارد" }),
      icon: CreditCardIcon,
      color: "#d32f2f",
      bgColor: "error.light",
    },
    {
      id: "wallet",
      label: t("e_wallet", { defaultValue: "محفظة إلكترونية" }),
      icon: AccountBalanceWalletIcon,
      color: "#ed6c02",
      bgColor: "warning.light",
    },
  ];

  return (
    <Box dir={dir}>
      <Grid container spacing={2}>
        {paymentOptions.map((option) => {
          const isSelected = selected === option.id;
          const Icon = option.icon;

          return (
            <Grid item xs={12} sm={6} md={3} key={option.id}>
              <Paper
                onClick={() => handleChange(option.id)}
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: "2px solid",
                  borderColor: isSelected ? "primary.main" : "grey.200",
                  bgcolor: isSelected ? "primary.50" : "background.paper",
                  cursor: "pointer",
                  position: "relative",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    borderColor: isSelected ? "primary.dark" : "primary.light",
                    boxShadow: isSelected
                      ? "0 8px 24px rgba(213, 170, 47, 0.2)"
                      : "0 4px 16px rgba(0, 0, 0, 0.08)",
                    transform: "translateY(-4px)",
                  },
                  "&::before": isSelected
                    ? {
                        content: '""',
                        position: "absolute",
                        top: -2,
                        left: -2,
                        right: -2,
                        bottom: -2,
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                        opacity: 0.15,
                        zIndex: -1,
                      }
                    : {},
                }}
              >
                {/* Radio Button */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: lang === "ar" ? 12 : "auto",
                    left: lang === "ar" ? "auto" : 12,
                  }}
                >
                  <Radio
                    checked={isSelected}
                    sx={{
                      p: 0,
                      color: "grey.400",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                </Box>

                {/* Icon */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 2,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      bgcolor: isSelected ? option.bgColor : "grey.50",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                      transform: isSelected ? "scale(1.1)" : "scale(1)",
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 36,
                        color: isSelected ? option.color : "grey.500",
                        transition: "color 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>

                {/* Label */}
                <Typography
                  variant="subtitle2"
                  align="center"
                  sx={{
                    fontWeight: isSelected ? 800 : 600,
                    color: isSelected ? "primary.dark" : "text.secondary",
                    transition: "all 0.3s ease",
                    fontSize: "0.95rem",
                  }}
                >
                  {option.label}
                </Typography>

                {/* Selected Badge */}
                {isSelected && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      borderRadius: "0 0 10px 10px",
                      background:
                        "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 100%)",
                      animation: "slideIn 0.4s ease-out",
                      "@keyframes slideIn": {
                        from: {
                          transform: "scaleX(0)",
                        },
                        to: {
                          transform: "scaleX(1)",
                        },
                      },
                    }}
                  />
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
